import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'homesanty-scroll-positions';

const readStoredPositions = () => {
    try {
        const storedPositions = window.sessionStorage.getItem(STORAGE_KEY);
        return storedPositions ? JSON.parse(storedPositions) : {};
    } catch {
        return {};
    }
};

const buildUrlKey = (location) => `${location.pathname}${location.search}${location.hash}`;

const getLocationKeys = (location) => [location.key, buildUrlKey(location)].filter(Boolean);

const ScrollToTop = () => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const positionsRef = useRef({});

    const persistPositions = useCallback(() => {
        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positionsRef.current));
        } catch {
            // Ignore storage write failures.
        }
    }, []);

    const saveScrollPosition = useCallback((targetLocation) => {
        const position = {
            left: window.scrollX,
            top: window.scrollY,
        };

        getLocationKeys(targetLocation).forEach((key) => {
            positionsRef.current[key] = position;
        });

        persistPositions();
    }, [persistPositions]);

    useEffect(() => {
        positionsRef.current = readStoredPositions();
    }, []);

    useEffect(() => {
        const previousScrollRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';

        return () => {
            window.history.scrollRestoration = previousScrollRestoration;
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => {
            saveScrollPosition(location);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [location, saveScrollPosition]);

    useLayoutEffect(() => {
        const savedPosition = getLocationKeys(location)
            .map((key) => positionsRef.current[key])
            .find(Boolean);

        // const appContent = document.getElementById('app-content');
        // if (appContent) {
        //     appContent.style.opacity = '0';
        //     appContent.style.pointerEvents = 'none';
        // }

        let attempts = 0;
        const MAX_ATTEMPTS = 40; // ~2 seconds at 50ms per attempt
        let rafId = null;
        let loadListener = null;

        function tryRestoreScroll() {
            attempts++;
            if (navigationType === 'POP' && savedPosition) {
                const maxScrollTop = Math.max(
                    document.documentElement.scrollHeight - window.innerHeight,
                    0
                );
                const nextTop = Math.min(savedPosition.top, maxScrollTop);
                window.scrollTo({ left: savedPosition.left, top: nextTop, behavior: 'auto' });
                // If we can't scroll to the desired position, try again
                if (Math.abs(window.scrollY - nextTop) > 2 && attempts < MAX_ATTEMPTS) {
                    rafId = window.requestAnimationFrame(tryRestoreScroll);
                    return;
                }
            } else if (location.hash) {
                const element = document.getElementById(location.hash.slice(1));
                if (element) {
                    element.scrollIntoView();
                } else {
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                }
            } else {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
            // if (appContent) {
            //     appContent.style.opacity = '';
            //     appContent.style.pointerEvents = '';
            // }
            // Removed setRestored(true) as it's no longer needed
            if (loadListener) window.removeEventListener('load', loadListener);
        }

        // Fallback: if images load after attempts, restore again
        loadListener = () => {
            if (navigationType === 'POP' && savedPosition) {
                const maxScrollTop = Math.max(
                    document.documentElement.scrollHeight - window.innerHeight,
                    0
                );
                const nextTop = Math.min(savedPosition.top, maxScrollTop);
                window.scrollTo({ left: savedPosition.left, top: nextTop, behavior: 'auto' });
            }
            // if (appContent) {
            //     appContent.style.opacity = '';
            //     appContent.style.pointerEvents = '';
            // }
            // Removed setRestored(true) as it's no longer needed
        };
        window.addEventListener('load', loadListener);

        tryRestoreScroll();

        return () => {
            saveScrollPosition(location);
            // if (appContent) {
            //     appContent.style.opacity = '';
            //     appContent.style.pointerEvents = '';
            // }
            if (rafId) window.cancelAnimationFrame(rafId);
            if (loadListener) window.removeEventListener('load', loadListener);
        };
    }, [location, navigationType, saveScrollPosition]);

    return null;
};

export default ScrollToTop;
